/**
 * Platform Service Orders — inquiry-style orders submitted by
 * customer studios for services offered in `platformServices`.
 *
 * v1 is "request a quote" (no payments): the studio fills a short
 * form on the service card, the doc lands here with `status: 'new'`,
 * the platform admin sees it in `/admin/io-orders` and contacts the
 * studio out-of-band (phone / email) to confirm + price.
 *
 * Status workflow:
 *   new       → admin hasn't touched it yet
 *   contacted → admin called / emailed the studio
 *   in_progress → service is being delivered
 *   completed → service done, optional invoice attached later
 *   cancelled → studio backed out OR admin couldn't service the request
 *
 * Schema is denormalised on purpose: service title + category +
 * studio name + contact info are copied at order-creation time so
 * the admin view doesn't need cross-collection joins, and so the
 * order keeps the snapshot even if the catalogue entry changes.
 */
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';
import type { PlatformServiceCategory } from '@/lib/platformServices';

// ───── Status ─────────────────────────────────────────────────

export type PlatformServiceOrderStatus =
  | 'new'
  | 'contacted'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

export type PlatformServiceOrderStatusConfig = {
  key: PlatformServiceOrderStatus;
  label: string;
  color: string;      // pill text
  tintBg: string;     // pill bg
};

export const PLATFORM_SERVICE_ORDER_STATUSES: PlatformServiceOrderStatusConfig[] = [
  { key: 'new',         label: 'New',         color: '#1D4ED8', tintBg: '#DBEAFE' },
  { key: 'contacted',   label: 'Contacted',   color: '#7C3AED', tintBg: '#F5F3FF' },
  { key: 'in_progress', label: 'In progress', color: '#D97706', tintBg: '#FEF3C7' },
  { key: 'completed',   label: 'Completed',   color: '#047857', tintBg: '#D1FAE5' },
  { key: 'cancelled',   label: 'Cancelled',   color: '#B91C1C', tintBg: '#FEE2E2' },
];

export function getPlatformServiceOrderStatusConfig(
  key: PlatformServiceOrderStatus | string,
): PlatformServiceOrderStatusConfig {
  return (
    PLATFORM_SERVICE_ORDER_STATUSES.find((s) => s.key === key) ??
    PLATFORM_SERVICE_ORDER_STATUSES[0]
  );
}

// ───── Status history entry ──────────────────────────────────

export type PlatformServiceOrderStatusEvent = {
  status: PlatformServiceOrderStatus;
  /** Epoch millis (client-side stamped because Firestore rejects
   *  `serverTimestamp()` inside arrayUnion). */
  at: number;
  /** Uid of the user who changed status (admin or studio). */
  by: string;
  /** Display name of the user — denormed at write time. */
  byName?: string;
  /** Optional admin note attached to the status change. */
  note?: string;
};

// ───── Order shape ────────────────────────────────────────────

export type PlatformServiceOrder = {
  id: string;

  // Service snapshot (denormalised — survives catalogue edits)
  serviceId: string;
  serviceTitle: string;
  serviceCategory: PlatformServiceCategory;
  servicePriceLabel: string;

  // Studio (org) snapshot
  orgId: string;
  orgName: string;

  // Requesting user
  requestedByUid: string;
  requestedByName: string;
  requestedByPhone?: string;
  requestedByEmail?: string;

  // Optional project link
  projectId?: string;
  projectName?: string;

  // Contact + site details
  contactName: string;
  contactPhone: string;
  contactEmail?: string;
  serviceAddress?: string;
  preferredDate?: string;   // ISO yyyy-MM-dd
  notes?: string;

  // Lifecycle
  status: PlatformServiceOrderStatus;
  adminNotes?: string;
  quotedPrice?: number;
  statusHistory: PlatformServiceOrderStatusEvent[];

  createdAt: { toMillis(): number } | null;
  updatedAt?: { toMillis(): number } | null;
};

// ───── Writes ────────────────────────────────────────────────

export type CreatePlatformServiceOrderInput = {
  // Service snapshot
  serviceId: string;
  serviceTitle: string;
  serviceCategory: PlatformServiceCategory;
  servicePriceLabel: string;

  // Org snapshot
  orgId: string;
  orgName: string;

  // Requesting user
  requestedByUid: string;
  requestedByName: string;
  requestedByPhone?: string;
  requestedByEmail?: string;

  // Optional project
  projectId?: string;
  projectName?: string;

  // Form fields
  contactName: string;
  contactPhone: string;
  contactEmail?: string;
  serviceAddress?: string;
  preferredDate?: string;
  notes?: string;
};

export async function createPlatformServiceOrder(
  input: CreatePlatformServiceOrderInput,
): Promise<string> {
  const now = Date.now();
  const firstEvent: PlatformServiceOrderStatusEvent = {
    status: 'new',
    at: now,
    by: input.requestedByUid,
    byName: input.requestedByName,
  };

  const payload: Record<string, unknown> = {
    serviceId: input.serviceId,
    serviceTitle: input.serviceTitle,
    serviceCategory: input.serviceCategory,
    servicePriceLabel: input.servicePriceLabel,

    orgId: input.orgId,
    orgName: input.orgName,

    requestedByUid: input.requestedByUid,
    requestedByName: input.requestedByName,

    contactName: input.contactName.trim(),
    contactPhone: input.contactPhone.trim(),

    status: 'new' as PlatformServiceOrderStatus,
    statusHistory: [firstEvent],

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const phone = input.requestedByPhone?.trim();
  if (phone) payload.requestedByPhone = phone;
  const email = input.requestedByEmail?.trim();
  if (email) payload.requestedByEmail = email;

  if (input.projectId) payload.projectId = input.projectId;
  const pn = input.projectName?.trim();
  if (pn) payload.projectName = pn;

  const ce = input.contactEmail?.trim();
  if (ce) payload.contactEmail = ce;
  const sa = input.serviceAddress?.trim();
  if (sa) payload.serviceAddress = sa;
  if (input.preferredDate) payload.preferredDate = input.preferredDate;
  const n = input.notes?.trim();
  if (n) payload.notes = n;

  const ref = await addDoc(collection(db, 'platformServiceOrders'), payload);
  return ref.id;
}

/** Admin status change — appends to statusHistory atomically. */
export async function updatePlatformServiceOrderStatus(
  orderId: string,
  next: PlatformServiceOrderStatus,
  actor: { uid: string; name?: string },
  options?: { note?: string; adminNotes?: string; quotedPrice?: number },
): Promise<void> {
  const event: PlatformServiceOrderStatusEvent = {
    status: next,
    at: Date.now(),
    by: actor.uid,
  };
  if (actor.name) event.byName = actor.name;
  if (options?.note?.trim()) event.note = options.note.trim();

  const updates: Record<string, unknown> = {
    status: next,
    statusHistory: arrayUnion(event),
    updatedAt: serverTimestamp(),
  };
  if (options?.adminNotes !== undefined) {
    const v = options.adminNotes.trim();
    updates.adminNotes = v || deleteField();
  }
  if (options?.quotedPrice !== undefined) {
    updates.quotedPrice =
      options.quotedPrice > 0 ? options.quotedPrice : deleteField();
  }
  await updateDoc(doc(db, 'platformServiceOrders', orderId), updates);
}

export async function deletePlatformServiceOrder(
  orderId: string,
): Promise<void> {
  await deleteDoc(doc(db, 'platformServiceOrders', orderId));
}
